import React, { useCallback, useEffect, useMemo } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { useAssetsState } from '../../stores/AssetsProvider'
import { columns, numberFilter } from './filterConfig'
import StructureIcon from '../Icons/StructureIcon'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import TableProvider, { useTableState } from '../Table/TableProvider'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { t } from 'i18next'
import OverflowTooltip from '../../components/OverflowTooltip.jsx'

const StyledStructureButton = styled.button`
  border: 0;
  padding: 0;
  background: none;
`

const defaultSorted = [
  {
    dataField: 'displayName',
    order: 'desc',
  },
]

const AssetsTableInner = ({ noActions, onRowClick, withCheckbox, tableId }) => {
  const {
    assets,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    selectedRow,
    setShowAllAssets,
    setSorting,
    tableIsLoading,
    setOpenOfficeFilter,
    setOpenCostCenterFilter,
    setOpenGroupFilter,
    setShowOverlayLeft,
    resetFilter,
    filterAsset,
    setFilterAsset,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
  } = useAssetsState()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()

  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })

  useEffect(() => {
    //after Creating new Asset auto output him
    if (!!filterAsset) {
      setTimeout(() => {
        resetFilter()
        //set text in number searchbar
        numberFilter(filterAsset)
        //set filter + trigger call
        setFilter((prev) => ({
          ...prev,
          'filter.number': filterAsset,
        }))
      }, 50)
      setFilterAsset(null)
    }
  }, [filterAsset, setFilterAsset, resetFilter, setFilter])

  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllAssets(false)
      const { filters } = newState
      setFilter((prev) => ({
        ...prev,
        'filter.displayName': filters.displayName
          ? filters.displayName.filterVal
          : '',
        'filter.number': filters.number ? filters.number.filterVal : '',
        'filter.officeDisplayName': filters['office.displayName']
          ? filters['office.displayName'].filterVal
          : '',
        'filter.costCenterDisplayName': filters['costCenter.displayName']
          ? filters['costCenter.displayName'].filterVal
          : '',
        'filter.groupDisplayName': filters['group.displayName']
          ? filters['group.displayName'].filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '-' : ''}${sortField}`)
    }
    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }

  const handleStructureClick = useCallback(
    (structureType) => (e) => {
      e.stopPropagation()
      switch (structureType) {
        case 'costCenter.displayName':
          setOpenCostCenterFilter((prev) => !prev)
          setOpenOfficeFilter(false)
          setOpenGroupFilter(false)
          setShowOverlayLeft(true)
          break
        case 'office.displayName':
          setOpenCostCenterFilter(false)
          setOpenGroupFilter(false)
          setOpenOfficeFilter((prev) => !prev)
          setShowOverlayLeft(true)
          break
        case 'group.displayName':
          setOpenCostCenterFilter(false)
          setOpenOfficeFilter(false)
          setOpenGroupFilter((prev) => !prev)
          setShowOverlayLeft(true)
          break
        default:
      }
    },
    [
      setOpenCostCenterFilter,
      setOpenOfficeFilter,
      setOpenGroupFilter,
      setShowOverlayLeft,
    ]
  )

  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allCheckBoxesSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          case 'group.displayName':
          case 'costCenter.displayName':
          case 'office.displayName':
            return (
              <>
                <OverflowTooltip text={column.text}>
                  {column.text}
                  {components.sortElement}
                  <div className='d-flex align-items-end'>
                    {components.filterElement}
                    <div className='ps-2'>
                      <StyledStructureButton
                        onClick={handleStructureClick(tmpColumn.dataField)}
                      >
                        <StructureIcon />
                      </StyledStructureButton>
                    </div>
                  </div>
                </OverflowTooltip>
              </>
            )
          default:
            return (
              <>
                {column.text}
                {components.sortElement}
                <div className='d-flex align-items-end'>
                  {components.filterElement}
                </div>
              </>
            )
        }
      }
    })

    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })

    const ignoreColumns = () => {
      var result = []
      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')
      return result
    }

    return tmpColumns.filter(
      (item) => !ignoreColumns().includes(item.dataField)
    )
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    handleStructureClick,
    noActions,
    withCheckbox,
  ])

  const getChosenFilters = () => {
    var message = []
    //save filters in Array
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('addAssetTable:alertMsg')
    var officeFilter = ''
    var costCenterFilter = ''
    var groupFilter = ''

    //returns Alert if no results for used filters
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      if (chosenFilters.includes('filter.officeIds'))
        officeFilter = t('addAssetTable:officeFilterAlertMsg')
      if (chosenFilters.includes('filter.costCenterIds'))
        costCenterFilter = t('addAssetTable:costCenterFilterAlertMsg')
      if (chosenFilters.includes('filter.groupIds'))
        groupFilter = t('addAssetTable:groupFilterAlertMsg')
      return (
        <div className='no-data-placeholder-alert'>
          {officeFilter && <Alert variant='info'>{officeFilter}</Alert>}
          {costCenterFilter && <Alert variant='info'>{costCenterFilter}</Alert>}
          {groupFilter && <Alert variant='info'>{groupFilter}</Alert>}
          {!costCenterFilter && !officeFilter && !groupFilter && (
            <Alert variant='info'>{alertMessage}</Alert>
          )}
        </div>
      )
    }
    //returns empty Table
    else {
      return (
        <div className='no-data-placeholder'>
          {t('addAssetTable:pleaseSearchAsset')}
        </div>
      )
    }
  }

  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={assets}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const AssetsTable = ({ noActions, onRowClick, withCheckbox, tableId }) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useAssetsState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <AssetsTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default AssetsTable
